import { useEffect, useRef, useState } from 'react';
import { getJobEvents } from '../api';

const POLL_INTERVAL_MS = 2000;

export interface ActivityStep {
  /** The agent's tool-call id — stable across polls, so React keys don't churn. */
  callId: string;
  label: string;
  /** True once the matching tool_finished event has arrived. */
  done: boolean;
}

/**
 * The live trail of what an agent is doing, for the chat's pending bubble.
 *
 * The backend records a `tool_started` / `tool_finished` pair per tool call
 * (see `ai_agent/activity.py`); this pairs them by call id so the UI can show
 * finished steps dimmed and the current one spinning. Pass `null` when nothing
 * is running — polling stops and the trail clears, so the next turn starts empty.
 */
export function useJobActivity(jobId: string | null | undefined): ActivityStep[] {
  const [steps, setSteps] = useState<ActivityStep[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!jobId) {
      setSteps([]);
      return;
    }

    let cancelled = false;

    const fetchEvents = async () => {
      // A hidden tab skips the round trip; the endpoint returns the whole
      // trail every time, so the next poll catches up in one shot.
      if (cancelled || document.hidden || inFlightRef.current) {
        if (!cancelled) timerRef.current = setTimeout(fetchEvents, POLL_INTERVAL_MS);
        return;
      }
      inFlightRef.current = true;

      try {
        const res = await getJobEvents(jobId);
        if (cancelled) return;

        const finished = new Set(
          res.events
            .filter((e) => e.event_type === 'tool_finished')
            .map((e) => e.data.call_id)
            .filter((id): id is string => Boolean(id))
        );
        setSteps(
          res.events
            .filter((e) => e.event_type === 'tool_started' && e.data.label)
            // Models can open several tool calls at once, and those rows race
            // each other to commit. `step` is the order the runner actually
            // started them in; arrival order is not.
            .sort((a, b) => (a.data.step ?? 0) - (b.data.step ?? 0))
            .map((e, i) => ({
              callId: e.data.call_id || `step-${i}`,
              label: e.data.label as string,
              done: finished.has(e.data.call_id ?? ''),
            }))
        );

        if (res.job_status === 'completed' || res.job_status === 'failed') return;
      } catch {
        // A dropped poll is not worth surfacing — the trail is decoration on
        // top of the job status the caller is already polling.
      } finally {
        inFlightRef.current = false;
      }

      if (!cancelled) timerRef.current = setTimeout(fetchEvents, POLL_INTERVAL_MS);
    };

    void fetchEvents();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [jobId]);

  return steps;
}
